import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { client, unwrap } from '../api/client';

const API_BASE = import.meta.env.VITE_API_URL ?? '';
const EVENTS = [
  'message.created',
  'message.status',
  'conversation.updated',
  'conversation.deleted',
  'deal.changed',
  'notification.created',
] as const;
export type RealtimeEventName = (typeof EVENTS)[number];

type Listener = (event: RealtimeEventName, data: Record<string, unknown>) => void;
const listeners = new Set<Listener>();

/** Suscripción a eventos en tiempo real desde cualquier componente (sin abrir otra conexión). */
export function useRealtimeListener(listener: Listener): void {
  const ref = useRef(listener);
  useEffect(() => {
    ref.current = listener;
  });
  useEffect(() => {
    const wrapped: Listener = (event, data) => ref.current(event, data);
    listeners.add(wrapped);
    return () => void listeners.delete(wrapped);
  }, []);
}

/**
 * Conexión única de Server-Sent Events para la sesión. `EventSource` no admite cabeceras, así que
 * se pide un token de 60 s con la sesión y se reconecta (con token nuevo) si la conexión se cae.
 * Cada evento invalida las consultas afectadas: el inbox se actualiza sin recargar.
 */
export function useRealtimeConnection(enabled: boolean): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;
    let source: EventSource | null = null;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;

    const dispatch = (event: RealtimeEventName, data: Record<string, unknown>) => {
      const conversationId = typeof data.conversationId === 'string' ? data.conversationId : null;
      if (event !== 'notification.created')
        void queryClient.invalidateQueries({ queryKey: ['inbox', 'conversations'] });
      if (conversationId) {
        void queryClient.invalidateQueries({ queryKey: ['inbox', 'conversation', conversationId] });
        if (event !== 'conversation.updated')
          void queryClient.invalidateQueries({ queryKey: ['inbox', 'messages', conversationId] });
      }
      if (event === 'deal.changed') void queryClient.invalidateQueries({ queryKey: ['crm'] });
      if (event === 'notification.created')
        void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      for (const listener of listeners) listener(event, data);
    };

    const connect = async () => {
      try {
        const { token } = await unwrap(client.POST('/v1/realtime/token'));
        if (stopped) return;
        source = new EventSource(
          `${API_BASE}/v1/realtime/stream?token=${encodeURIComponent(token)}`,
        );
        for (const name of EVENTS)
          source.addEventListener(name, (message) => {
            try {
              dispatch(
                name,
                JSON.parse((message as MessageEvent<string>).data) as Record<string, unknown>,
              );
            } catch {
              /* evento mal formado: se ignora */
            }
          });
        source.onerror = () => {
          source?.close();
          if (!stopped) retry = setTimeout(() => void connect(), 5000);
        };
      } catch {
        if (!stopped) retry = setTimeout(() => void connect(), 15000);
      }
    };
    void connect();
    return () => {
      stopped = true;
      clearTimeout(retry);
      source?.close();
    };
  }, [enabled, queryClient]);
}
